type ValueType = "string" | "number" | "boolean" | "null";

export interface PropertySchema {
  type: ValueType | ValueType[];
  minLength?: number;
  pattern?: string;
  minimum?: number;
  exclusiveMinimum?: number;
  description?: string;
}

export interface ConfigSchema {
  $schema?: string;
  $id?: string;
  type: "object";
  additionalProperties: false;
  required: string[];
  properties: Record<string, PropertySchema>;
}

export const mergeSchemas = (...schemas: ConfigSchema[]): ConfigSchema => {
  const merged: ConfigSchema = {
    type: "object",
    additionalProperties: false,
    required: [],
    properties: {},
  };
  for (const schema of schemas) {
    for (const [key, property] of Object.entries(schema.properties)) {
      if (Object.prototype.hasOwnProperty.call(merged.properties, key))
        throw new Error(
          `Config key "${key}" is defined by more than one schema.`
        );
      merged.properties[key] = property;
    }
    merged.required.push(...schema.required);
  }
  return merged;
};

export const validateAppConfig = (
  raw: unknown,
  schema: ConfigSchema
): string[] => {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw))
    return ["Configuration must be a JSON object."];
  const config = raw as Record<string, unknown>;
  const errors: string[] = [];

  for (const key of schema.required)
    if (!(key in config)) errors.push(`Missing required key "${key}".`);

  for (const [key, value] of Object.entries(config)) {
    const property = Object.prototype.hasOwnProperty.call(
      schema.properties,
      key
    )
      ? schema.properties[key]
      : undefined;
    if (!property) {
      errors.push(`Unknown key "${key}".`);
      continue;
    }

    const allowed = Array.isArray(property.type)
      ? property.type
      : [property.type];
    const actual = value === null ? "null" : typeof value;
    if (!allowed.includes(actual as ValueType)) {
      errors.push(
        `Key "${key}" must be ${allowed.join(" or ")}, got ${actual}.`
      );
      continue;
    }
    if (typeof value === "string") {
      if (property.minLength !== undefined && value.length < property.minLength)
        errors.push(
          `Key "${key}" must be at least ${property.minLength} characters.`
        );
      if (
        property.pattern !== undefined &&
        !new RegExp(property.pattern).test(value)
      )
        errors.push(`Key "${key}" does not match ${property.pattern}.`);
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value))
        errors.push(`Key "${key}" must be a finite number.`);
      if (property.minimum !== undefined && value < property.minimum)
        errors.push(`Key "${key}" must be >= ${property.minimum}.`);
      if (
        property.exclusiveMinimum !== undefined &&
        value <= property.exclusiveMinimum
      )
        errors.push(`Key "${key}" must be > ${property.exclusiveMinimum}.`);
    }
  }
  return errors;
};
